// Argus — file_read helper. Bloc ws-direct-batch-2 cleanup 2026-05-05: drop REST flow complet.
// `readFilePreview` cere WS direct exclusiv. Pentru >10 MB → throw WsFileTooLargeError la caller.
// Caller decide cum afișează (mesaj „Fișier prea mare pentru preview, max 10 MB").

import { wsClient, type FileReadResult as WsFileReadResult } from './wsClient';

const TEXT_EXTENSIONS = new Set([
  // Plain text + docs
  'txt', 'log', 'md', 'markdown', 'rst', 'adoc', 'asciidoc', 'tex', 'latex',
  // Tabular data
  'csv', 'tsv', 'jsonl', 'ndjson',
  // Config + properties
  'ini', 'cfg', 'conf', 'config', 'toml', 'env', 'properties', 'editorconfig',
  'gitignore', 'gitattributes', 'dockerignore', 'npmrc', 'nvmrc', 'prettierrc', 'eslintrc',
  // Shell
  'sh', 'bash', 'zsh', 'fish', 'bashrc', 'profile', 'ps1', 'psm1', 'psd1', 'bat', 'cmd',
  // Data
  'json', 'json5', 'xml', 'yaml', 'yml',
  // Web
  'html', 'htm', 'css', 'scss', 'sass', 'less', 'vue', 'svelte', 'astro',
  'js', 'mjs', 'cjs', 'jsx', 'ts', 'tsx',
  // Programming languages
  'py', 'rb', 'go', 'rs', 'java', 'kt', 'kts', 'c', 'h', 'cpp', 'cxx', 'hpp', 'hxx',
  'cs', 'swift', 'dart', 'scala', 'lua', 'php', 'pl', 'pm',
  'fs', 'fsx', 'fsi', 'ml', 'mli', 'hs', 'lhs', 'ex', 'exs', 'erl', 'hrl', 'clj', 'cljs', 'edn',
  'r', 'jl', 'tcl', 'vb', 'vbs',
  // Build/CI
  'gradle', 'groovy', 'sbt', 'cmake', 'mk', 'makefile',
  // Database / API
  'sql', 'graphql', 'gql', 'prisma', 'http', 'rest',
  // Patches
  'diff', 'patch',
]);

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'tiff']);

const VIDEO_EXTENSIONS = new Set(['mp4', 'webm']);

const VIDEO_UNSUPPORTED_EXTENSIONS = new Set(['mkv', 'avi', 'mov', 'wmv', 'flv', 'm2ts', 'mts', 'ts']);

/**
 * Bloc ws-direct-batch-2 cleanup 2026-05-05: descriptor preview pure WS (NO REST fallback).
 * Pentru >10 MB → caller catch WsFileTooLargeError + afișează mesaj „prea mare pentru preview".
 */
export interface FilePreviewData {
  /** Data URL (image/video/binary) generat din base64 sau text decoded gol pentru kind='text'. */
  readonly downloadUrl: string;
  /** Text decoded (pentru kind='text'). null pentru image/video/binary. */
  readonly textContent: string | null;
  readonly mimeType: string;
  readonly size: number;
  readonly fileName: string;
}

/**
 * Citește file via WS direct exclusiv (≤10 MB inline base64). Pentru >10 MB throws
 * `WsFileTooLargeError` — caller decide UI mesaj.
 *
 * @param kind — decide cum decodez content-ul WS (text → string, altele → Blob URL).
 */
export async function readFilePreview(
  agentId: string,
  path: string,
  justification: string,
  kind: PreviewKind,
): Promise<FilePreviewData> {
  const wsResult = await wsClient.requestFileRead(agentId, path, justification);
  return wsResultToPreview(wsResult, kind);
}

function wsResultToPreview(ws: WsFileReadResult, kind: PreviewKind): FilePreviewData {
  // Decode base64 → Blob → URL pentru image/video/binary; UTF-8 string pentru text.
  if (kind === 'text') {
    const bytes = base64ToBytes(ws.content);
    const decoder = new TextDecoder('utf-8', { fatal: false });
    return {
      downloadUrl: '',
      textContent: decoder.decode(bytes),
      mimeType: ws.mimeType,
      size: ws.size,
      fileName: ws.fileName,
    };
  }
  // image / video / binary → Blob URL.
  const bytes = base64ToBytes(ws.content);
  const ab: ArrayBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  const blob = new Blob([ab], { type: ws.mimeType });
  const url = URL.createObjectURL(blob);
  return {
    downloadUrl: url,
    textContent: null,
    mimeType: ws.mimeType,
    size: ws.size,
    fileName: ws.fileName,
  };
}

function base64ToBytes(b64: string): Uint8Array {
  const binStr = atob(b64);
  const len = binStr.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binStr.charCodeAt(i);
  return bytes;
}

export type PreviewKind = 'text' | 'image' | 'video' | 'video_unsupported' | 'binary';

export function previewKindFromName(name: string): PreviewKind {
  const lower = name.toLowerCase();
  // Filename-only matches (no extension) — Dockerfile, Makefile etc.
  if (lower === 'dockerfile' || lower === 'makefile' || lower.startsWith('.env')) {
    return 'text';
  }
  const idx = name.lastIndexOf('.');
  if (idx < 0) return 'binary';
  const ext = name.substring(idx + 1).toLowerCase();
  if (TEXT_EXTENSIONS.has(ext)) return 'text';
  if (IMAGE_EXTENSIONS.has(ext)) return 'image';
  if (VIDEO_EXTENSIONS.has(ext)) return 'video';
  if (VIDEO_UNSUPPORTED_EXTENSIONS.has(ext)) return 'video_unsupported';
  return 'binary';
}
